import { useMemo, useState } from "react";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";
import { GlobalState } from "context/GlobalContext";
import { BQ_MASTER_CACHE_KEYS } from "../../shared/bqMasterDropdownCache";
import {
	emptyPoItemRow,
	itemDescriptionFromMaster,
	parseItemRate,
	poMasterFetches,
	syncPoItemDraft,
} from "../data";
import { PoPanel, poColors, poPrimaryBtnSx, poSecondaryBtnSx } from "../poUi";
import PoLineItemsAddModal from "./PoLineItemsAddModal";
import PoLineItemsUploadModal from "./PoLineItemsUploadModal";

const ensureRowId = (row, index) => ({
	...emptyPoItemRow(),
	...row,
	id: row?.id,
	is_deleted: !!row?.is_deleted,
	_rowId: row?._rowId || row?.id || `po-item-${index}-${row?.barcode || row?.item_code || "x"}-${Date.now() % 1e6}`,
});

const isPersistedRow = (row) => row?.id != null && row?.id !== "";

const itemMatchesBarcode = (row, barcode) => {
	const b = String(barcode);
	return String(row?.barcode ?? "") === b || String(row?.key ?? "") === b;
};

/** Resolve barcode → { barcode, item_code, item_description, rate } from cache or masters/items. */
const resolveBarcodeRow = async (barcode, getDropdownCache) => {
	const code = String(barcode || "").trim();
	if (!code) return null;

	const cached = getDropdownCache?.(BQ_MASTER_CACHE_KEYS.itemsPo) || [];
	const fromCache = cached.find((row) => itemMatchesBarcode(row, code));
	if (fromCache?.item_code) {
		return {
			barcode: fromCache.barcode || code,
			item_code: String(fromCache.item_code),
			item_description: fromCache.item_description || itemDescriptionFromMaster(fromCache) || "",
			rate: parseItemRate(fromCache.rate ?? fromCache.cost),
		};
	}

	try {
		const res = await poMasterFetches.barcode(0, 20, code);
		const rows = res?.data?.result || [];
		const exact = rows.find((row) => itemMatchesBarcode(row, code)) || rows[0];
		if (!exact?.item_code) return null;
		return {
			barcode: exact.barcode || code,
			item_code: String(exact.item_code),
			item_description: exact.item_description || itemDescriptionFromMaster(exact) || "",
			rate: parseItemRate(exact.rate),
		};
	} catch {
		return null;
	}
};

const PoLineItemsSection = ({
	items = [],
	onChange,
	readOnly = false,
	/** Optional toolbar actions (replaces default Upload / Add item). */
	toolbarActions = null,
	/** Appended after toolbar / default actions (e.g. Cancel + Save). */
	extraToolbarActions = null,
	/** Skip outer PoPanel (e.g. inside an edit modal). */
	flat = false,
	tableMinHeight,
}) => {
	const { snackBar, setSnackBar, setSnackBarOpen, getDropdownCache } = GlobalState();
	const [addModal, setAddModal] = useState(false);
	const [uploadModal, setUploadModal] = useState(false);

	// Soft-deleted rows stay in `items` for save, but never render in the grid.
	const allRows = useMemo(() => (items || []).map((row, i) => ensureRowId(row, i)), [items]);
	const rows = useMemo(() => allRows.filter((row) => !row.is_deleted), [allRows]);
	const activeCount = rows.length;

	const notify = (message, severity = "success") => {
		setSnackBarOpen(true);
		setSnackBar({
			...snackBar,
			message,
			severity,
			anchorOrigin: { horizontal: "center", vertical: "top" },
		});
	};

	const commitRows = (next) => {
		if (typeof onChange === "function") onChange(next);
	};

	const handleAddItem = async (draftItem) => {
		const synced = syncPoItemDraft(draftItem);
		const barcode = String(synced.barcode || "").trim();
		if (!barcode) {
			notify("Barcode is required", "warning");
			return false;
		}

		let item_code = String(synced.item_code || "").trim();
		let item_description = String(synced.item_description || "").trim();
		let rate = parseItemRate(synced.rate);
		if (!item_code || !item_description) {
			const hit = await resolveBarcodeRow(barcode, getDropdownCache);
			if (!hit) {
				notify(`Unknown barcode: ${barcode}`, "error");
				return false;
			}
			item_code = item_code || hit.item_code;
			item_description = item_description || hit.item_description || "";
			if (rate == null) rate = hit.rate;
		}

		const qty = Number(synced.qty);
		commitRows([
			...allRows,
			ensureRowId(
				{
					barcode,
					item_description,
					item_code,
					qty: Number.isFinite(qty) && qty > 0 ? qty : 1,
					rate,
					due_date: synced.due_date || null,
					remarks: synced.remarks || "",
					is_deleted: false,
				},
				allRows.length
			),
		]);
		notify("Item added");
		return true;
	};

	/** Soft-delete persisted rows (hidden in UI, kept for save); drop unsaved new rows. */
	const handleRemove = (rowId) => {
		const next = [];
		allRows.forEach((row) => {
			if (row._rowId !== rowId) {
				next.push(row);
				return;
			}
			if (isPersistedRow(row)) {
				next.push({ ...row, is_deleted: true });
			}
		});
		commitRows(next);
	};

	const handleUploadConfirm = (uploadedRows) => {
		const existingBarcodes = new Set(rows.map((r) => String(r.barcode)));
		const merged = [...allRows];
		let added = 0;
		(uploadedRows || []).forEach((row, index) => {
			if (existingBarcodes.has(String(row.barcode))) return;
			existingBarcodes.add(String(row.barcode));
			merged.push(ensureRowId({ ...row, is_deleted: false }, merged.length + index));
			added += 1;
		});
		commitRows(merged);
		notify(`Uploaded ${added} new line${added === 1 ? "" : "s"}`);
		return true;
	};

	const tableColumns = useMemo(
		() => [
			{
				key: "po_item_no",
				name: "PO Item No",
				minWidth: 150,
				align: "center",
				headerAlign: "center",
				valueGetter: ({ row }) => row.po_item_no || "—",
			},
			{
				key: "barcode",
				name: "Barcode",
				minWidth: 150,
				align: "center",
				headerAlign: "center",
				valueGetter: ({ row }) => row.barcode || "—",
			},
			...(!readOnly
				? [
						{
							key: "item_description",
							name: "Item Description",
							minWidth: 200,
							align: "center",
							headerAlign: "center",
							valueGetter: ({ row }) => row.item_description || "—",
						},
				  ]
				: []),
			{
				key: "item_code",
				name: "Item Code",
				minWidth: 140,
				align: "center",
				headerAlign: "center",
			},
			{
				key: "qty",
				name: "Qty",
				minWidth: 90,
				align: "center",
				headerAlign: "center",
			},
			{
				key: "rate",
				name: "Rate",
				minWidth: 90,
				align: "center",
				headerAlign: "center",
				valueGetter: ({ row }) => (row.rate == null || row.rate === "" ? "—" : row.rate),
			},
			{
				key: "ginesys_po_item_id",
				name: "Ginesys Item Id",
				minWidth: 120,
				align: "center",
				headerAlign: "center",
				valueGetter: ({ row }) =>
					row.ginesys_po_item_id == null || row.ginesys_po_item_id === "" ? "—" : row.ginesys_po_item_id,
			},
			{
				key: "due_date",
				name: "Due Date",
				minWidth: 120,
				align: "center",
				headerAlign: "center",
				valueGetter: ({ row }) => (row.due_date ? String(row.due_date).slice(0, 10) : "—"),
			},
			{
				key: "remarks",
				name: "Remarks",
				minWidth: 160,
				align: "center",
				headerAlign: "center",
				valueGetter: ({ row }) => row.remarks || "—",
			},
			...(!readOnly
				? [
						{
							key: "actions",
							name: "Actions",
							minWidth: 90,
							align: "center",
							headerAlign: "center",
							renderCell: ({ row }) => (
								<IconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										handleRemove(row._rowId);
									}}
									sx={{
										color: poColors.muted,
										"&:hover": { color: poColors.danger, bgcolor: poColors.dangerBg },
									}}
									aria-label="Remove item">
									<DeleteOutlineIcon fontSize="small" />
								</IconButton>
							),
						},
				  ]
				: []),
		],
		[readOnly, allRows]
	);

	const tableHeight = Math.max(
		tableMinHeight ?? 200,
		Math.min(520, 56 + Math.max(rows.length, 3) * 44)
	);

	const defaultActions = !readOnly ? (
		<Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
			<Button
				size="small"
				startIcon={<UploadFileIcon />}
				onClick={() => setUploadModal(true)}
				sx={poSecondaryBtnSx}
				variant="outlined">
				Upload
			</Button>
			<Button
				size="small"
				variant="contained"
				startIcon={<AddIcon />}
				onClick={() => setAddModal(true)}
				sx={poPrimaryBtnSx}>
				Add item
			</Button>
		</Stack>
	) : null;

	const mainActions = toolbarActions !== null ? toolbarActions : defaultActions;
	const actions =
		mainActions || extraToolbarActions ? (
			<Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
				{mainActions}
				{extraToolbarActions}
			</Stack>
		) : null;

	const grid = (
		<Box sx={{ height: tableHeight, p: flat ? 0 : 0.5 }}>
			<TanstackDatagrid
				columns={tableColumns}
				pageData={rows}
				setPageData={() => {}}
				rowCount={rows.length}
				setRowCount={() => {}}
				serverSidePagination={false}
				disableColumnFilter
				disableColumnSort
				disablePagination
				isLoading={false}
				emptyMessage="No line items yet"
				getRowId={(row) => row._rowId}
				rowHeight={40}
				headerHeight={42}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						gap: 1.5,
						width: "100%",
						minWidth: 0,
						pr: 0.5,
					}}>
					<Stack spacing={0.1} minWidth={0}>
						<Typography sx={{ fontSize: 13, fontWeight: 700, color: poColors.ink, letterSpacing: "0.01em" }}>
							Item Details
						</Typography>
						{activeCount > 0 && (
							<Typography sx={{ fontSize: 11.5, color: poColors.muted, fontWeight: 500 }}>
								{activeCount} line{activeCount === 1 ? "" : "s"}
							</Typography>
						)}
					</Stack>
					{actions}
				</Box>
			</TanstackDatagrid>
		</Box>
	);

	return (
		<>
			{flat ? grid : <PoPanel contentSx={{ px: 0, py: 0 }}>{grid}</PoPanel>}

			{!readOnly && (
				<>
					<PoLineItemsAddModal open={addModal} onClose={() => setAddModal(false)} onAdd={handleAddItem} />
					<PoLineItemsUploadModal
						modal={uploadModal}
						setModal={setUploadModal}
						existingBarcodes={rows.map((r) => r.barcode)}
						onConfirm={handleUploadConfirm}
					/>
				</>
			)}
		</>
	);
};

export default PoLineItemsSection;
